import { Fragment, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TableMui from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import { formatDate, formatCNPJ } from 'utils/formatDate';
import { setContractingList, setFilterCurrentPage, setFilterRowsPerPage } from 'v3/data/store/payment/actions';
import { setIntallmentContractingKey } from 'v3/data/store/installments/actions';
import { StoreProfileEnum } from 'v3/data/store/stores/enums';
import { PAGE_INITIAL_VALUE, ROWS_PER_PAGE_OPTION } from 'common/constants';
import { useStoreSelector } from 'v3/data/store';
import { showErrorDialog } from 'v3/data/store/error/actions';
import { IStoreItem } from 'v3/data/store/stores/types';
import { useBreakpoints } from 'v3/hooks/useBreakpoints';
import * as api from 'v3/services/api';
import { NoItemsFound } from 'v3/shared/components/NoItemsFound';
import { SubtitleMedium } from 'v3/shared/typography/SubtitleMedium';
import { theme } from 'v3/styles/global-styles';
import {
  AlignStatusContainer,
  PaperStyled,
  TableCellGroup,
  TableCellStyled,
  TablePaginationStyles,
  TableRowStyled,
} from '../styles';
import { TextButton } from 'v3/shared/components/Button/TextButton';
import { PixRounded } from '@mui/icons-material';
import { PaymentsMobileTable } from 'v3/shared/components/MobileTable/PaymentsMobileTable';
import { ContractingStateEnum } from 'v3/data/store/contracting/enums';
import { maskCPF } from 'utils/formatters';
import { RoutesEnum } from 'v3/routes/enum';
import { SellerVerificationModal } from 'v3/shared/components/Modal/SellerVerificationModal';

const FIXED_SEARCH_STATUS = ContractingStateEnum.ContractingFinished;

export const PaymentsTable = () => {
  const { items, count } = useStoreSelector((store) => store.payment.list);
  const { filter } = useStoreSelector((store) => store.payment);
  const { id, group } = useStoreSelector((store) => store.store.storeLogged);
  const { storeList } = useStoreSelector((store) => store.store);
  const { contractingKey } = useStoreSelector((store) => store.installments);

  const { rowsPerPage, page } = filter;
  const navigate = useNavigate();
  const { isDesktop } = useBreakpoints();

  const [openSellerVerificationModal, setOpenSellerVerificationModal] = useState(false);

  const isStore = group === StoreProfileEnum.STORE;

  const handleNavigation = (key: string) => {
    navigate(`${RoutesEnum.Payments}/${key}`);
  };

  const filterOnlyCheckedStores = (storeList: IStoreItem[]) => {
    const desiredStoreList = storeList.filter((store) => store.checked === true);
    return desiredStoreList.map((store) => store.id);
  };

  const getContractingList = useCallback(async () => {
    const currentFilters = { ...filter };

    const removeEmptyProperties = Object.fromEntries(
      Object.entries(currentFilters).filter(([key, value]) => value !== undefined && value !== null && value !== ''),
    );

    const desiredStoreIds = filterOnlyCheckedStores(storeList);

    try {
      const response = await api.getContractingList({
        ...removeEmptyProperties,
        status: FIXED_SEARCH_STATUS,
        storeIds: [...desiredStoreIds],
        storeId: id,
      });
      setContractingList(response.data);
    } catch (error) {
      showErrorDialog('Erro ao obter lista de contratações', error);
    }
  }, [filter, storeList, id]);

  useEffect(() => {
    id && (filter.storeIds.length > 0 || isStore) && getContractingList();
  }, [getContractingList, id, filter.storeIds.length, isStore]);

  const handleOpenSellerVerificationModal = (key: string) => {
    setIntallmentContractingKey(key);
    setOpenSellerVerificationModal(true);
  };

  const handleCloseSellerVerificationModal = () => {
    setOpenSellerVerificationModal(false);
    setIntallmentContractingKey('');
  };

  return (
    <Fragment>
      {items.isEmpty ? (
        <NoItemsFound />
      ) : isDesktop ? (
        <PaperStyled>
          <TableContainer sx={{ maxHeight: 450 }}>
            <TableMui stickyHeader aria-label="sticky tables">
              <TableHead>
                <TableRow>
                  <TableCellStyled>Cliente</TableCellStyled>
                  <TableCellStyled>Loja</TableCellStyled>
                  <TableCellStyled>Data e hora</TableCellStyled>
                  <TableCellStyled>Pagamento</TableCellStyled>
                </TableRow>
              </TableHead>

              <TableBody>
                {items.map((item, index) => {
                  const { date, time } = formatDate(item.createdAt);

                  return (
                    <TableRowStyled
                      hover
                      key={item.contractingKey}
                    >
                      <TableCellGroup key={`name-cpf-${index}`}>
                        <SubtitleMedium>{item.person.fullName}</SubtitleMedium>
                        <SubtitleMedium color={theme.palette.secondary.main}>{maskCPF(item.person.cpf)}</SubtitleMedium>
                      </TableCellGroup>

                      <TableCellGroup key={`storeName-cnpj-${index}`}>
                        <SubtitleMedium>{item.creditSimulation.store.name}</SubtitleMedium>
                        <SubtitleMedium color={theme.palette.secondary.main}>
                          {formatCNPJ(item.creditSimulation.store.cnpj)}
                        </SubtitleMedium>
                      </TableCellGroup>

                      <TableCellGroup key={`date-time-${index}`}>
                        <SubtitleMedium>{date}</SubtitleMedium>
                        <SubtitleMedium color={theme.palette.secondary.main}>{time}</SubtitleMedium>
                      </TableCellGroup>

                      <TableCellGroup key={`status-${index}`}>
                        <AlignStatusContainer>
                          <TextButton
                            text="Pagar com Pix"
                            $outline={true}
                            startIcon={PixRounded}
                            size="small"
                            handleClick={() => {
                              handleOpenSellerVerificationModal(item.contractingKey);
                            }}
                          />
                        </AlignStatusContainer>
                      </TableCellGroup>
                    </TableRowStyled>
                  );
                })}
              </TableBody>
            </TableMui>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={ROWS_PER_PAGE_OPTION}
            component="div"
            sx={TablePaginationStyles}
            count={count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(event, value) => {
              setFilterCurrentPage(value);
            }}
            onRowsPerPageChange={(event) => {
              setFilterCurrentPage(PAGE_INITIAL_VALUE);
              setFilterRowsPerPage(Number(event.target.value));
            }}
          />
        </PaperStyled>
      ) : (
        <PaymentsMobileTable
          handleOpenSellerVerificationModal={handleOpenSellerVerificationModal}
          contractingList={items}
        />
      )}
      <SellerVerificationModal modal={{
        active: openSellerVerificationModal,
        title: 'Consultor, identifique-se antes de continuar',
        cancelButtonText: 'Cancelar',
        confirmButtonText: 'Aceitar',
      }}
        onClose={handleCloseSellerVerificationModal}
        onConfirm={() => {
          if (contractingKey) {
            handleNavigation(contractingKey);
          }
          handleCloseSellerVerificationModal();
        }}
      />
    </Fragment>
  );
};
